import React from 'react';
import styled from 'styled-components';
import useTranslation from 'next-translate/useTranslation';
import { FiEye } from 'react-icons/fi';
import { useRouter } from 'next/router';
import { MapPin } from 'react-feather';
import { Track, style, useCurrentUser } from '../../api';
import UserImage from '../common/UserImage';
import { ProfileLink } from '../common/Link';
import { TextBold12, TextRegular12, TextRegular14 } from '../common/Text';
import { FollowButton } from '../common/Buttons/V2';
import formatLocation from '../../utils/formatLocation';
import { NoDesktop } from '../../style/constants';
import { ExpandableText } from '../common/ExpandableText';
import { Button } from '../../VuexyDesign/atoms/_index';

const TrackUserInfoMobile = ({ track }: { track: Track }) => {
  const { t } = useTranslation('tracks');
  const { user } = track;
  const {
    total_plays,
    display_name,
    age,
    role,
    city,
    country,
    about,
    profile_photo,
  } = user;
  const router = useRouter();
  const currentUser = useCurrentUser();

  const viewProfile = () => {
    router.push(`/profile/${user.id}`);
  };

  return (
    <Wrapper className="border">
      <NoDesktop>
        <UserInfoSection>
          <div className="d-flex justify-content-between">
            <UserInfoWrapper>
              <ProfileImageLink profileId={user.id}>
                <UserImage src={profile_photo} size={32} />
              </ProfileImageLink>
              <NameAndPlays>
                <ProfileLink profileId={user.id}>
                  <TextBold12>{display_name}</TextBold12>
                </ProfileLink>
                <Plays>
                  {total_plays} {t('tracks:plays') as string}
                </Plays>
              </NameAndPlays>
            </UserInfoWrapper>
            <div>
              {user.id !== currentUser.id ? (
                <FollowButton userId={user.id} buttonColor="primary" />
              ) : (
                <ViewProfile
                  outline
                  className="br-5"
                  size="sm"
                  onClick={() => viewProfile()}
                >
                  <FiEye size={14} className="mr-1 text-dark" />
                  <TextBold12 className="text-dark">
                    {t('tracks:viewProfile') as string}
                  </TextBold12>
                </ViewProfile>
              )}
            </div>
          </div>
          <LocationAndDescription>
            <LocationAndAge>
              <GreyText style={{ textTransform: 'capitalize' }} as="div">
                {age}, {role}
              </GreyText>
              <LocationWrapper>
                <MapPin size={16} color={style.grey_dark_new} />
                <GreyText
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatLocation(city, country?.name)}
                </GreyText>
              </LocationWrapper>
            </LocationAndAge>
            {about && <ExpandableText>{about}</ExpandableText>}
          </LocationAndDescription>
        </UserInfoSection>
      </NoDesktop>
    </Wrapper>
  );
};

export default TrackUserInfoMobile;

const LocationAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LocationAndAge = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const UserInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const ViewProfile = styled(Button)`
  padding: 8px 12px;
  border: 1.5px solid #d8d8d8 !important;
  border-radius: 5px;
  display: flex;

  .text-dark {
    white-space: nowrap;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 15px;
  margin-top: 16px;
`;

const UserInfoWrapper = styled.div`
  display: flex;
`;

const NameAndPlays = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  margin-left: 12px;
`;

const Plays = styled(TextRegular12)`
  color: ${style.grey_dark_new};
`;

const GreyText = styled(TextRegular14)`
  color: ${style.grey_dark_new};
`;

const LocationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ProfileImageLink = styled(ProfileLink)`
  display: flex;
  height: 100%;
  align-items: center;
`;
